import { Cell, Grid, Typography } from '@pickupp/ui/core'
import { makeStyles, useTheme } from '@pickupp/ui/styles'
import cx from 'classnames'
import PropTypes from 'prop-types'
import React from 'react'

import Icon from '~/src/components/Icon'
import Link from '~/src/components/Link'

import RegionSwitcher from './RegionSwitcher'
import styles from './styles'

const useStyles = makeStyles(styles)

const FooterLogo = ({ logo }) => {
  const theme = useTheme()

  return (
    <Link to="/">
      <img
        loading="lazy"
        src={logo.src?.data?.attributes?.url}
        alt={logo.alt}
        width={theme.global.logoWidth}
      />
    </Link>
  )
}

FooterLogo.propTypes = {
  logo: PropTypes.object.isRequired,
}

const FooterNavigation = ({ columns, logo }) => {
  const classes = useStyles()
  return (
    <div
      className={classes.navigationContainer}
    >
      <div className={classes.footerLogo}>
        {logo && <FooterLogo logo={logo} />}
      </div>
      <div className={classes.columnContainerWrapper}>
        {columns?.map((column) => (
          <Cell
            key={column.label}
            className={classes.columnContainer}
            md
          >
            <div>
              {column.link ? (
                <Link
                  to={column.link.url}
                  target={column.link.target}
                  className={cx(
                    classes.link,
                    classes.noUnderline,
                  )}
                  data-identity={column.link.identity}
                  event_name={column.link.event_name}
                >
                  <Typography variant="inherit" weight={700}>
                    {column.link.text || column.label}
                  </Typography>
                </Link>
              ) : (
                <Typography
                  variant="inherit"
                  weight={700}
                >
                  {column.label}
                </Typography>
              )}
            </div>
            <div className={classes.menuContainer}>
              {column.items?.map((item, index) => (
                <React.Fragment key={index}>
                  <div>
                    {item.link ? (
                      <Link
                        key={item.label || item.link.text}
                        to={item.link.url}
                        target={item.link.target}
                        className={classes.link}
                        data-identity={item.link.identity}
                        event_name={item.link.event_name}
                      >
                        <Typography variant="inherit">
                          {item.link.text || item.label}
                        </Typography>
                      </Link>
                    ) : (
                      <Typography
                        key={item.label}
                        variant="inherit"
                      >
                        {item.label}
                      </Typography>
                    )}
                  </div>
                  {item.items?.length > 0 && (
                    <ul className={classes.subMenuContainer}>
                      {item.items.map((subItem, i) => (
                        subItem.link ? (
                          <li key={i}>
                            <Link
                              key={subItem.label || subItem.link.text}
                              to={subItem.link.url}
                              target={subItem.link.target}
                              className={classes.link}
                              data-identity={subItem.link.identity}
                              event_name={subItem.link.event_name}
                            >
                              <Typography variant="inherit">
                                {subItem.link.text || subItem.label}
                              </Typography>
                            </Link>
                          </li>
                        ) : (
                          <li>
                            <Typography
                              key={subItem.label}
                              variant="inherit"
                            >
                              {subItem.label}
                            </Typography>
                          </li>
                        )))}
                    </ul>
                  )}
                </React.Fragment>
              ))}
            </div>
          </Cell>
        ))}
      </div>
    </div>
  )
}

FooterNavigation.propTypes = {
  columns: PropTypes.array.isRequired,
}

const FooterPrimary = ({
  logo,
  columns,
}) => {
  const classes = useStyles()
  return (
    <Grid justify="space-between" className={classes.primaryContent}>
      <FooterNavigation columns={columns} logo={logo} />
    </Grid>
  )
}

FooterPrimary.propTypes = {
  logo: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
}

const FooterCopyright = ({ copyrights }) => {
  const classes = useStyles()

  return (
    <div className={classes.copyrightContainer}>
      {copyrights?.map(({ content }, index) => {
        if (content === 'region_options') {
          return <RegionSwitcher key="region-switcher" />
        }
        return <Typography key={index} html={content} />
      })}
    </div>
  )
}

FooterCopyright.propTypes = {
  copyrights: PropTypes.array.isRequired,
}

const FooterSocial = ({ socials }) => {
  const classes = useStyles()

  return (
    <div className={classes.socialContainer}>
      {socials.map(({ link, icon }) => (
        <Link
          key={link.url}
          to={link.url}
          target={link.target}
          rel="noopener noreferrer"
          className={classes.social}
          data-identity={link.identity}
          event_name={link.event_name}
        >
          <Icon icon={icon} />
        </Link>
      ))}
    </div>
  )
}

FooterSocial.propTypes = {
  socials: PropTypes.array.isRequired,
}

const FooterSecondary = ({ buttons, copyrights, socials }) => {
  const classes = useStyles()

  return (
    <div className={classes.secondaryContainer}>
      <Grid spacing={2} className={classes.secondaryContent}>
        <Cell
          sm={12}
          md={8}
          className={classes.footerCopyright}
        >
          <FooterCopyright copyrights={copyrights} />
        </Cell>
        <Cell
          sm={12}
          md={4}
          className={classes.footerSocial}
        >
          {buttons?.length > 0 && (
            <>
              <Typography
                className={classes.buttonsTitle}
                align="center"
                variant="inherit"
                weight="bold"
                color="white"
              >
                Download Now
              </Typography>
              <div className={classes.buttonsContainer}>
                {buttons?.map((button, index) => (
                  <Link key={index} to={button.link ? button.link.url : '/'}>
                    <img
                      className={classes.button}
                      loading="lazy"
                      src={button.src?.data?.attributes?.url}
                      alt={button.alt}
                    />
                  </Link>
                ))}
              </div>
            </>
          )}
          {socials && (
            <FooterSocial socials={socials} />
          )}
        </Cell>
      </Grid>
    </div>
  )
}

FooterSecondary.propTypes = {
  buttons: PropTypes.array,
  copyrights: PropTypes.array.isRequired,
  socials: PropTypes.array.isRequired,
}

FooterSecondary.defaultProps = {
  buttons: [],
}

const Footer = ({
  logo,
  columns,
  buttons,
  copyright,
  socials,
}) => {
  return (
    <footer>
      <Grid>
        <Cell
          xs={12}
        >
          <FooterPrimary
            logo={logo}
            columns={columns}
          />
        </Cell>
        <Cell
          xs={12}
        >
          <FooterSecondary
            copyrights={copyright}
            socials={socials}
            buttons={buttons}
          />
        </Cell>
      </Grid>
    </footer>
  )
}

Footer.propTypes = {
  logo: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  copyright: PropTypes.array.isRequired,
  socials: PropTypes.array.isRequired,
}

export default Footer
